import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, getCurrentUser, signIn, signOut } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

export type UserRole = 'distributor' | 'reseller' | 'customer';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  contextData: any;
  isAuthenticated: boolean;
  hasRole: (roles: UserRole | UserRole[]) => boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function getUserContextData(user: User) {
  if (!user) return null;

  if (user.role === 'reseller') {
    const { data: resellerData } = await supabase
      .from('reseller_users')
      .select('reseller_id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (resellerData?.reseller_id) {
      const { data: reseller } = await supabase
        .from('resellers')
        .select('*')
        .eq('reseller_id', resellerData.reseller_id)
        .maybeSingle();

      return { ...user, reseller };
    }
  }

  else if (user.role === 'customer') {
    const { data: customerData } = await supabase
      .from('customer_users')
      .select('customer_id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (customerData?.customer_id) {
      const { data: customer } = await supabase
        .from('customers')
        .select('*, reseller:resellers(*)')
        .eq('customer_id', customerData.customer_id)
        .maybeSingle();

      return { ...user, customer };
    }
  }

  // Distributors stored only in auth.users — no enrichment needed
  return user;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [contextData, setContextData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const hasRole = (roles: UserRole | UserRole[]): boolean => {
    if (!user) return false;
    return Array.isArray(roles)
      ? roles.includes(user.role)
      : user.role === roles;
  };

  const loadExtendedData = async (currentUser: User) => {
    try {
      const extendedData = await getUserContextData(currentUser);
      setContextData(extendedData);
    } catch (err) {
      console.error('Failed to load extended user data:', err);
    }
  };

  useEffect(() => {
    async function loadUser() {
      try {
        const currentUser = await getCurrentUser();
        setUser(currentUser);
        if (currentUser) {
          await loadExtendedData(currentUser);
        }
      } catch (err) {
        setError('Failed to load user session');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }

    loadUser();
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const user = await signIn(email, password);
      if (!user) throw new Error('Authentication failed');
      setUser(user);
      await loadExtendedData(user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await signOut();
      setUser(null);
      setContextData(null);
      navigate('/login');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Logout failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        error,
        contextData,
        isAuthenticated: !!user,
        hasRole,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
